import { z } from "zod";

import {
  VIBE_SHIP_GATE_MODE_DEFAULT,
  VibeShipGateModeReadSchema,
  VibeShipGateModeSchema
} from "../../../../shared/domain/vibe/ship-gate-mode";

/**
 * Org-wide defaults a NEW Vibe app inherits at creation.
 *
 * ## Inherited at creation, never consulted live
 *
 * The deploy path reads the APP's own `shipGateMode` and never this record, so
 * changing a default here cannot retroactively change what an existing app
 * does. That is the same bug class the deployment-level freeze prevents, one
 * level up: a value read at the wrong moment re-decides something already
 * decided.
 *
 * The consequence, stated because a reader will assume whichever they expected:
 * **an app CAN sit at `OFF` under an org default of `ENFORCE`.** This is a
 * DEFAULT, not a FLOOR. A floor would have to be consulted at deploy time to
 * mean anything, which is precisely the thing above.
 *
 * Without it, every new app starts ungated and the opt-in has to be re-made by
 * hand each time — which is how a safety default stays off across a whole
 * organization.
 */
export const VibeOrgAppDefaultsSchema = z.object({
  /// The ship-gate mode a newly created app starts at. `OFF` for an
  /// organization that has configured nothing, which is stored as no row at all
  /// rather than as a row saying `OFF`.
  ///
  /// `.default("OFF")` for the deploy-skew reason documented on
  /// `VibeAppSchema.shipGateMode`.
  shipGateMode: VibeShipGateModeReadSchema.default(VIBE_SHIP_GATE_MODE_DEFAULT),
  /// When the organization last changed these defaults, or `null` when it never
  /// has. Null is the honest rendering of "no row": a console that showed a
  /// fabricated timestamp beside an inherited default would say someone chose
  /// `OFF` when nobody did.
  updatedAt: z.coerce.date().nullable()
});

export type VibeOrgAppDefaultsDto = z.infer<typeof VibeOrgAppDefaultsSchema>;

export const GetVibeOrgAppDefaultsResponseSchema = z.object({
  defaults: VibeOrgAppDefaultsSchema
});

export type GetVibeOrgAppDefaultsResponse = z.infer<typeof GetVibeOrgAppDefaultsResponseSchema>;

/**
 * Set the org-wide defaults. A full PUT rather than a PATCH: there is one field
 * today, and a caller that means "leave the rest alone" has nothing else to
 * leave alone. When a second default joins (`requireApprovals` has the identical
 * gap), this becomes the same whole-object write `UpdateVibeApp` uses for
 * quotas and health checks — validated in one shot rather than field by field.
 */
export const SetVibeOrgAppDefaultsBodySchema = z.object({
  shipGateMode: VibeShipGateModeSchema
});

export type SetVibeOrgAppDefaultsBody = z.infer<typeof SetVibeOrgAppDefaultsBodySchema>;

export const SetVibeOrgAppDefaultsResponseSchema = z.object({
  defaults: VibeOrgAppDefaultsSchema
});

export type SetVibeOrgAppDefaultsResponse = z.infer<typeof SetVibeOrgAppDefaultsResponseSchema>;
